import { makeStyles } from '@material-ui/styles';
import { grayColor, whiteColor } from 'assets/jss/material-dashboard-pro-react';
import { FakeUserIcon } from 'components';
import Card from 'components/Card/Card';
import CardBody from 'components/Card/CardBody';
import CardHeader from 'components/Card/CardHeader';
import GridContainer from 'components/Grid/GridContainer';
import GridItem from 'components/Grid/GridItem';
import { get } from 'helpers/apiHelpers';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, useHistory } from 'react-router-dom';
import Button from '@material-ui/core/Button/Button';
import { Divider } from '@material-ui/core';
// @ts-expect-error - no types available for this package
import BigCalendar from 'react-big-calendar';
import CalendarHeader from 'views/Orders/Diets/Details/Calendar/CalendarHeader';
import MonthDayCell from 'views/Orders/Diets/Details/Calendar/MonthDayCell';
import WeekdayHeader from 'views/Orders/Diets/Details/Calendar/WeekdayHeader';
import moment, { type Moment } from 'moment';
import AddDietCalendarTile from 'views/Orders/Diets/Details/Calendar/AddDietCalendarTile';
import useIsDayDisabled from 'views/Orders/Diets/Details/Calendar/utils/useIsDayDisabled';
import { nonOperatingEvent } from 'views/Orders/Diets/Details/Calendar/calendarEvents';

type NoDietsCalendarProps = {
  userId: string;
};

type Client = {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  homeDomain: string;
};

type Event = {
  title: string | JSX.Element;
  start: Date;
  end: Date;
  color?: string;
  shadow?: string;
  height?: string;
  fontColor?: string;
};

const useStyles = makeStyles({
  title: {
    margin: '12px 0 0 0',
    fontSize: '16px',
    fontWeight: '400',
    lineHeight: '1',
    color: grayColor[1],
  },
  box: {
    background: '#f3f3f3',
    padding: '24px',
    marginTop: '16px',
    borderRadius: '10px',
    textAlign: 'center',
  },
});

const localizer = BigCalendar.momentLocalizer(moment);

const NoDietsCalendar = (props: NoDietsCalendarProps) => {
  const { userId } = props;
  const { t } = useTranslation();
  const history = useHistory();
  const isDayDisabled = useIsDayDisabled();

  const [client, setClient] = useState<Client | null>(null);
  const [events, setEvents] = useState<Event[]>([]);

  const classes = useStyles();

  const newOrder = () => {
    history.push(`/admin/orders/add?userId=${userId}`);
  };

  const getEvents = useCallback(
    (start: Date, end: Date) => {
      const getAddDietHref = (day: Moment) => {
        const params = new URLSearchParams({
          userId,
          date: day.format('YYYY-MM-DD'),
        });

        const url = new URL('/admin/orders/add', window.location.origin);
        url.search = params.toString();

        return url.toString();
      };

      const startMoment = moment(start);
      const endMoment = moment(end);
      const daysDiff = endMoment.diff(startMoment, 'days');

      const days = [];

      for (let i = 0; i <= daysDiff; i += 1) {
        days.push(moment(startMoment).add(i, 'days'));
      }

      return days.map(day => {
        if (isDayDisabled(day.toDate())) {
          return nonOperatingEvent(day.toDate(), t);
        }

        return {
          start: day.toDate(),
          end: day.toDate(),
          title: <AddDietCalendarTile href={getAddDietHref(day)} />,
        };
      });
    },
    [isDayDisabled, t, userId]
  );

  const eventColors = (event: Event) => ({
    style: {
      backgroundColor: whiteColor,
      height: '120px',
      cursor: 'pointer',
      color: event.fontColor,
    },
  });

  useEffect(() => {
    const fetchClient = async () => {
      const res = await get(`clients/${userId}`);
      setClient(res);
    };

    if (userId) {
      fetchClient();
    }
  }, [userId]);

  useEffect(() => {
    const startOfMonth = moment(new Date()).startOf('month');
    const endOfMonth = moment(new Date()).endOf('month');
    setEvents(getEvents(startOfMonth.toDate(), endOfMonth.toDate()));
  }, [getEvents]);

  if (!client) {
    return null;
  }

  return (
    <Card>
      <CardHeader>
        <GridContainer>
          <GridItem md={2}>
            <p className={classes.title}>{t('common.client')}</p>
            <h1>
              <b>
                <NavLink to={`/admin/clients/edit/${client.id}`}>
                  {client.firstName} {client.lastName}
                </NavLink>
                {/* @ts-expect-error - FakeUserIcon has no TypeScript */}
                <FakeUserIcon email={client.email} domain={client.homeDomain} />
              </b>
              <div>
                <NavLink
                  to={`/admin/clients/edit/${client.id}`}
                >{`ID #${client.id}`}</NavLink>
              </div>
            </h1>
          </GridItem>
        </GridContainer>
        <Divider />
      </CardHeader>
      <CardBody>
        <GridContainer>
          <GridItem md={2}>
            <Button
              variant="outlined"
              fullWidth={true}
              onClick={() => newOrder()}
            >
              {t('orders.newOrder')} +
            </Button>
            <div className={classes.box}>{t('orders.noOrders')}</div>
          </GridItem>
          <GridItem md={7}>
            <Card>
              <CardBody calendar={true}>
                <BigCalendar
                  localizer={localizer}
                  components={{
                    toolbar: (toolbarProps: {
                      onNavigate: (action: string) => void;
                      label: string;
                    }) => <CalendarHeader {...toolbarProps} />,
                    month: {
                      dateHeader: MonthDayCell,
                      header: WeekdayHeader,
                    },
                  }}
                  events={events}
                  views={['month']}
                  onRangeChange={(view: { start: Date; end: Date }) => {
                    setEvents(getEvents(view.start, view.end));
                  }}
                  eventPropGetter={eventColors}
                  formats={{
                    dateFormat: 'D',
                  }}
                />
              </CardBody>
            </Card>
          </GridItem>
        </GridContainer>
      </CardBody>
    </Card>
  );
};

export default NoDietsCalendar;
